// /settings — tenant workspace settings. Tabs: Perfil · Personas · Operación ·
// Procesos · Privacidad · Equipo · Facturación · Actividad. Operación and
// Procesos are manage-only and are not offered at all to an Asesor (how work
// gets handed out, and what the funnel's stages ARE, are a manager's calls);
// Operación carries the assignment policies and their rules editor and NOTHING
// else (CP3 L), Procesos is the pipeline/stage editor plus each process's ficha.
// Personas manages the CONTACT field library; the lead half lives per process,
// in Procesos → Datos del proceso. Equipo carries the conversation-visibility
// wall, because the wall is about Bandeja and Bandeja is about the team. Perfil edits persist (saveProfile, display name only);
// Equipo invites/role-change/remove/transfer are all wired (destructive actions
// behind a confirm modal). Facturación is REAL (Phase 3a.4b): plan, invoice
// history and payment method come from GET /billing (our Stripe mirror) and
// "Gestionar pago" opens a Stripe Customer Portal session; the usage meters
// still come from GET /settings. Only tenant_owner holds billing.read, so other
// roles get a plain "solo el Dueño" note, not an error. The tab also
// carries the clinic's OWN fiscal identity (FiscalIdentityCard → PUT
// /settings/fiscal): the four SAT fields a CFDI 4.0 needs from the receptor.
// Those used to be operator-only and shown here locked, which meant a typo in
// the one field the SAT matches exactly was a support round-trip. PDF/XML
// downloads of the stamped CFDI land with the rest of 3b.
// The Equipo tab's role Selects offer only what the CALLER may hand out: the
// owner sees Asesor + Administrador, an admin sees Asesor (minting an admin is
// the owner-only `tenant.users.grant_admin` rail), and nobody sees Visor or
// Dueño — tenant_viewer is hidden product-wide and ownership moves by transfer.
// Actividad exports the audit log to CSV client-side. Per-user
// notification preferences are NOT here — they moved to "Mi cuenta →
// Notificaciones" so every role (not just owner/admin) can reach their own.
//
// Consolidation (owner-product-consolidation-spec.md): Settings is workspace/
// account only. The dead tenants.config surfaces that the engine never read were
// removed — the "Horarios" tab (real hours now live on the bot via Agentes →
// Ajustes), the "Marca" tab (accent/signature, never applied to app or bot), and
// the Perfil "Correo de contacto" + "Zona horaria" fields. Timezone returns when
// it's wired into the booking engine (phase 5).

import { useQuery, useQueryClient } from '@tanstack/react-query'
// Subpath import, never the @vysto/shared barrel.
import { CFDI_USES, FISCAL_REGIMENS } from '@vysto/shared/fiscal'
import type { AxiosError } from 'axios'
import { type ReactNode, useMemo, useState, useEffect } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'

import { ConsentSettings } from './ConsentSettings.js'
import { ConversationVisibilityCard } from './ConversationVisibilityCard.js'
import { FieldsSection } from './FieldsSection.js'
import { OperationsSection } from './OperationsSection.js'
import { PipelinesSection } from './PipelinesSection.js'
import {
  type AuditEvent,
  type BillingInvoiceRow,
  type BillingPaymentMethod,
  cancelInvitation,
  changeMemberRole,
  downloadCfdi,
  type FiscalEdit,
  fiscalProblemsOf,
  type FiscalProblemView,
  formatMxn,
  getBilling,
  getInvoicePayUrl,
  getSettings,
  inviteTeamMember,
  type ManagerInfo,
  openBillingPortal,
  removeMember,
  requestCancellation,
  resendInvitation,
  resetMemberMfa,
  sendMemberPasswordReset,
  type ConnectedChannelView,
  type ProfileField,
  saveFiscal,
  saveProfile,
  type SubscriptionStatus,
  type TeamMember,
  transferOwnership,
  uiRoleFor,
  type UsageMeter,
} from './settings-data.js'
import { useSession } from '../../auth/session.js'
import { useAuthStore } from '../../auth/store.js'
import {
  EmptyStateCard,
  Banner,
  Button,
  Chip,
  DataTable,
  type DataTableColumn,
  DropdownMenu,
  FilterBar,
  FilterButton,
  FormField,
  Input,
  type MenuItem,
  Modal,
  PageHeader,
  ProgressBar,
  SearchInput,
  Select,
  Spinner,
  Tabs,
  TabPanel,
  toast,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { QueryError } from '../../lib/QueryError.js'
import { matchesSearch } from '../../lib/search.js'

const SETTINGS_TABS = [
  { id: 'perfil', label: 'Perfil' },
  // Igual que 'embudos': el id es el valor de `?tab=` que carga un marcador, así
  // que sigue siendo 'campos' aunque la pestaña ya se lea "Personas". La mitad de
  // prospectos se mudó a Procesos → Datos del proceso (CP3 L).
  { id: 'campos', label: 'Personas' },
  { id: 'operacion', label: 'Operación' },
  // The id is the `?tab=` value a bookmark carries, so it stays `embudos`
  // even though the tab now reads "Procesos".
  { id: 'embudos', label: 'Procesos' },
  { id: 'privacidad', label: 'Privacidad' },
  { id: 'equipo', label: 'Equipo' },
  { id: 'facturacion', label: 'Facturación' },
  { id: 'actividad', label: 'Actividad' },
]

function LockIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-[12px]"
    >
      <rect x="3.5" y="11" width="17" height="10" rx="2.2" />
      <path d="M7.5 11V7.5a4.5 4.5 0 0 1 9 0V11" />
    </svg>
  )
}

function EditIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.1"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-[13px]"
    >
      <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  )
}

function SectionCard({
  title,
  sub,
  onEdit,
  children,
}: {
  title: string
  sub: string
  onEdit?: () => void
  children: ReactNode
}): JSX.Element {
  return (
    <section className="rounded-xl border border-line bg-surface">
      <div className="flex items-start gap-3 border-b border-line px-5 py-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-[14px] font-bold text-text">{title}</h2>
          <p className="mt-0.5 text-[12px] leading-snug text-text-2">{sub}</p>
        </div>
        {onEdit && (
          <Button variant="secondary" size="sm" onClick={onEdit}>
            <EditIcon />
            Editar
          </Button>
        )}
      </div>
      <div className="px-5 py-4">{children}</div>
    </section>
  )
}

/** Read-only view of the channels the agent answers on. The owner doesn't
 *  connect channels (that's the manager's job, in the operator console) — this
 *  is so they can SEE what's live and know the agent is reachable where they
 *  expect. */
function ChannelsSection({ channels }: { channels: ConnectedChannelView[] }): JSX.Element {
  return (
    <SectionCard
      title="Canales"
      sub="Por dónde responde tu agente. Los conecta tu gerente; aquí ves cuáles están activos."
    >
      {channels.length === 0 ? (
        <p className="text-[12.5px] text-text-2">
          Aún no hay canales conectados. Tu gerente los configura por ti; escríbele si esperas que
          ya estuvieran activos.
        </p>
      ) : (
        <ul className="flex flex-col">
          {channels.map((c) => (
            <li
              key={c.channel}
              className="flex flex-col gap-1 border-b border-line py-3 last:border-b-0 sm:flex-row sm:items-center sm:gap-4"
            >
              <span className="flex items-center gap-2 text-[12.5px] font-medium text-text sm:w-40 sm:flex-none">
                <span className="inline-block size-1.5 rounded-full bg-ok" aria-hidden />
                {c.label}
              </span>
              <span className="min-w-0 truncate text-[12.5px] text-text-2">{c.account}</span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  )
}

function ProfileSection({ fields }: { fields: ProfileField[] }): JSX.Element {
  const qc = useQueryClient()
  const valueFor = (label: string): string => {
    const v = fields.find((f) => f.label === label)?.value ?? ''
    return v === '—' ? '' : v
  }
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [displayName, setDisplayName] = useState('')

  function openEdit(): void {
    setDisplayName(valueFor('Nombre para mostrar'))
    setOpen(true)
  }

  async function save(): Promise<void> {
    // Only send fields with a value; the backend validates non-empty optionals.
    const edit: Parameters<typeof saveProfile>[0] = {}
    if (displayName.trim()) edit.displayName = displayName.trim()
    setBusy(true)
    try {
      await saveProfile(edit)
      await qc.invalidateQueries({ queryKey: ['settings'] })
      toast.ok('Perfil actualizado')
      setOpen(false)
    } catch (err) {
      toast.error('No se pudo guardar', {
        description: apiErrorMessage(err, 'Revisa los datos e inténtalo de nuevo.'),
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <SectionCard
      title="Perfil del hospital"
      sub="Cómo se identifica tu hospital dentro de INHERTA."
      onEdit={openEdit}
    >
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Editar perfil"
        subtitle="Los datos legales y el número de WhatsApp los gestiona tu gerente."
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button size="sm" className="ml-auto" disabled={busy} onClick={() => void save()}>
              {busy ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3.5">
          <FormField label="Nombre para mostrar" htmlFor="profile-name">
            <Input
              id="profile-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </FormField>
        </div>
      </Modal>

      <dl className="flex flex-col">
        {fields.map((f) => (
          <div
            key={f.label}
            className="flex flex-col gap-1 border-b border-line py-3 last:border-b-0 sm:flex-row sm:items-center sm:gap-4"
          >
            <dt className="flex items-center gap-1.5 text-[12px] text-text-2 sm:w-56 sm:flex-none">
              {f.label}
              {f.locked && <span className="text-text-3">{<LockIcon />}</span>}
            </dt>
            <dd className="min-w-0 text-[12.5px] font-medium text-text">
              {f.value}
              {f.locked && f.lockHint && (
                <p className="mt-0.5 text-[11px] font-normal text-text-3">{f.lockHint}</p>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </SectionCard>
  )
}

function DotsIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="size-[15px]">
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </svg>
  )
}

// ════ Equipo ════

/**
 * The roles this caller may actually hand out.
 *
 * "Visor" is gone from both Selects: tenant_viewer is hidden from the whole UI
 * (a read-only seat nobody could describe the job of), and offering it here was
 * the only way left to create one. Existing viewers keep working and keep their
 * "Visor" label everywhere they're displayed.
 *
 * "Administrador" only appears for the owner. `tenant.users.grant_admin` is
 * owner-only, so an admin who picked it got a 403 and a toast — the Select was
 * offering a role the API had already decided to refuse. "Dueño" is never here:
 * ownership moves by transfer ("Hacer Dueño"), never by assignment.
 */
function mintableRoles(canGrantAdmin: boolean): { value: string; label: string }[] {
  const roles = [{ value: 'agente', label: 'Asesor' }]
  if (canGrantAdmin) roles.push({ value: 'administrador', label: 'Administrador' })
  return roles
}

function TeamAvatar({ initials, pending }: { initials: string; pending?: boolean }): JSX.Element {
  return (
    <span
      aria-hidden
      className={`grid size-9 flex-none place-items-center rounded-full text-[11px] font-bold ${
        pending ? 'bg-field text-text-3' : 'bg-[image:var(--brand-gradient)] text-white'
      }`}
    >
      {initials}
    </span>
  )
}

function TeamSection({ members }: { members: TeamMember[] }): JSX.Element {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const session = useSession()
  const clearSession = useAuthStore((s) => s.clear)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [role, setRole] = useState('agente')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [roleEdit, setRoleEdit] = useState<TeamMember | null>(null)
  const [editRole, setEditRole] = useState('agente')
  const [transferTarget, setTransferTarget] = useState<TeamMember | null>(null)
  // A member or invitation queued for a destructive action (remove / cancel),
  // confirmed via a modal before it runs.
  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null)
  // Restablecer MFA asks for a written motive first (the same free-text reason
  // the platform console records), so it gets its own modal.
  const [mfaTarget, setMfaTarget] = useState<TeamMember | null>(null)
  const [mfaReason, setMfaReason] = useState('')
  const activeCount = members.filter((m) => m.status === 'active').length
  const pendingCount = members.filter((m) => m.status === 'pending').length
  // "1 miembros activos" is the sentence a solo Dueña read every time she opened
  // Equipo. Both halves agree with their count, and zero pending is stated in
  // words rather than as a bare "0".
  const activeLabel = activeCount === 1 ? '1 miembro activo' : `${activeCount} miembros activos`
  const pendingLabel =
    pendingCount === 0
      ? 'sin invitaciones pendientes'
      : pendingCount === 1
        ? '1 invitación pendiente'
        : `${pendingCount} invitaciones pendientes`
  // Only the owner sees "Hacer Dueño" (the API is owner-only anyway).
  const iAmOwner = members.some((m) => m.isYou && m.roleValue === 'tenant_owner')
  // Two readings of the same fact, both from the server: the JWT's membership
  // role (what the API will check) and the team row marked "Tú". Either one
  // saying "owner" is enough to offer the Administrador option.
  const canGrantAdmin = session?.canGrantAdmin === true || iAmOwner
  const roleOptions = mintableRoles(canGrantAdmin)

  // Run a team action, refetch on success, and toast the outcome. The failure
  // path shows the API's own message when it sent one — a permission refusal
  // already says which rule stopped you, in Spanish, and replacing that with
  // "No se pudo…" turned an explanation into a mystery.
  async function run(fn: () => Promise<void>, okMsg: string, failMsg: string): Promise<void> {
    try {
      await fn()
      await qc.invalidateQueries({ queryKey: ['settings'] })
      toast.ok(okMsg)
    } catch (err) {
      toast.error(apiErrorMessage(err, failMsg))
    }
  }

  function menuForMember(m: TeamMember): MenuItem[] {
    const items: MenuItem[] = []
    if (m.kind === 'member' && !m.isYou && m.roleValue !== 'tenant_owner') {
      items.push({
        label: 'Cambiar rol',
        onSelect: () => {
          // Start on their current role when it's still one we hand out;
          // otherwise on Asesor. An existing Visor (or, for an admin caller, an
          // Administrador) is not in the option list, and a Select whose value
          // matches no option renders as blank.
          const current = uiRoleFor(m.roleValue)
          setEditRole(roleOptions.some((r) => r.value === current) ? current : 'agente')
          setRoleEdit(m)
        },
      })
      if (iAmOwner) {
        items.push({ label: 'Hacer Dueño', onSelect: () => setTransferTarget(m) })
      }
    }
    // Account recovery, mirroring the API's rails so the menu never offers a
    // door onto a 403: never on your own row (both actions are self-serve in
    // "Mi cuenta"), and never on the Dueña's row unless you ARE the Dueña.
    if (m.kind === 'member' && !m.isYou && (m.roleValue !== 'tenant_owner' || iAmOwner)) {
      items.push({
        label: 'Enviar restablecimiento de contraseña',
        onSelect: () =>
          void run(
            () => sendMemberPasswordReset(m.id),
            'Correo de restablecimiento enviado',
            'No se pudo enviar el correo.',
          ),
      })
      items.push({
        label: 'Restablecer MFA…',
        onSelect: () => {
          setMfaReason('')
          setMfaTarget(m)
        },
      })
    }
    if (m.kind === 'invite') {
      items.push({
        label: 'Reenviar invitación',
        onSelect: () =>
          void run(() => resendInvitation(m.id), 'Invitación reenviada', 'No se pudo reenviar.'),
      })
      items.push({
        label: 'Cancelar invitación',
        danger: true,
        onSelect: () => setRemoveTarget(m),
      })
    } else if (!m.isYou && m.roleValue !== 'tenant_owner') {
      items.push({
        label: 'Quitar del equipo',
        danger: true,
        onSelect: () => setRemoveTarget(m),
      })
    }
    return items
  }

  async function submitRoleEdit(): Promise<void> {
    if (!roleEdit) return
    await run(
      () => changeMemberRole(roleEdit.id, editRole),
      'Rol actualizado',
      'No se pudo cambiar el rol.',
    )
    setRoleEdit(null)
  }

  async function submitTransfer(): Promise<void> {
    if (!transferTarget) return
    setBusy(true)
    try {
      await transferOwnership(transferTarget.id)
    } catch (err) {
      toast.error('No se pudo transferir', {
        description: apiErrorMessage(err, 'Inténtalo de nuevo.'),
      })
      setBusy(false)
      return
    }
    // The API revokes our session on success, so further requests would 401:
    // sign out locally and send us back to login as the new administrator.
    toast.ok('Listo, transferiste la propiedad', {
      description: 'Ahora eres administrador. Inicia sesión de nuevo.',
    })
    clearSession()
    void navigate('/login', { replace: true })
  }

  async function submitMfaReset(): Promise<void> {
    const target = mfaTarget
    if (!target || mfaReason.trim() === '') return
    await run(
      () => resetMemberMfa(target.id, mfaReason.trim()),
      'Verificación en dos pasos restablecida',
      'No se pudo restablecer la verificación.',
    )
    setMfaTarget(null)
    setMfaReason('')
  }

  async function submitRemove(): Promise<void> {
    const target = removeTarget
    if (!target) return
    if (target.kind === 'invite') {
      await run(() => cancelInvitation(target.id), 'Invitación cancelada', 'No se pudo cancelar.')
    } else {
      await run(() => removeMember(target.id), 'Miembro quitado del equipo', 'No se pudo quitar.')
    }
    setRemoveTarget(null)
  }

  function closeInvite(): void {
    setInviteOpen(false)
    setEmail('')
    setRole('agente')
  }

  async function submitInvite(): Promise<void> {
    if (!email.trim()) {
      toast.error('Falta el correo', { description: 'Escribe el correo de quien invitas.' })
      return
    }
    setBusy(true)
    try {
      await inviteTeamMember(email.trim(), role)
      await qc.invalidateQueries({ queryKey: ['settings'] })
      toast.ok('Invitación enviada', { description: `Le avisamos a ${email.trim()}.` })
      closeInvite()
    } catch (err) {
      // The server's own words: "Ya hay una invitación pendiente para ese
      // correo", "Solo el Dueño puede invitar administradores". Telling
      // them to "revisa el correo" instead sent them to fix the wrong thing.
      toast.error('No se pudo invitar', {
        description: apiErrorMessage(err, 'Revisa el correo e inténtalo de nuevo.'),
      })
    } finally {
      setBusy(false)
    }
  }

  const columns: DataTableColumn<TeamMember>[] = [
    {
      key: 'name',
      label: 'Miembro',
      render: (m) => (
        <span className="flex items-center gap-2.5">
          <TeamAvatar initials={m.initials} pending={m.status === 'pending'} />
          <span className="min-w-0">
            <span className="flex items-center gap-1.5 font-semibold text-text">
              {m.name}
              {m.isYou && (
                <span className="rounded-[5px] bg-field px-1.5 py-px text-[9.5px] font-bold uppercase tracking-[0.04em] text-text-3">
                  Tú
                </span>
              )}
            </span>
            <span className="block truncate text-[11.5px] text-text-3">{m.email}</span>
          </span>
        </span>
      ),
    },
    { key: 'role', label: 'Rol', render: (m) => <span className="text-text-2">{m.role}</span> },
    {
      key: 'status',
      label: 'Estado',
      render: (m) => (
        <Chip tone={m.status === 'active' ? 'ok' : 'warn'} dot>
          {m.status === 'active' ? 'Activo' : 'Invitación pendiente'}
        </Chip>
      ),
    },
    {
      key: 'lastActivity',
      label: 'Última actividad',
      render: (m) => <span className="text-[12px] text-text-3">{m.lastActivity}</span>,
    },
    {
      key: 'menu',
      label: '',
      align: 'right',
      render: (m) => {
        // Your own row has no actions (both recovery actions are self-serve in
        // "Mi cuenta", and you can't change or remove yourself), so on a solo
        // team the only row on screen carried a button that opened an empty
        // dropdown. No actions ⇒ no trigger.
        const items = menuForMember(m)
        if (items.length === 0) return null
        return (
          <DropdownMenu
            trigger={
              <button
                type="button"
                aria-label={`Acciones para ${m.name}`}
                className="inline-flex size-8 items-center justify-center rounded-lg text-text-3 transition-colors hover:bg-hover hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue"
              >
                <DotsIcon />
              </button>
            }
            items={items}
          />
        )
      },
    },
  ]

  // Solo mode is a supported way to run the product, not a setup step left
  // half-done: one line that says so and points at the door, without asking her
  // to walk through it.
  const soloOwner =
    members.length === 1 && members[0]?.isYou === true && members[0]?.roleValue === 'tenant_owner'

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[12.5px] text-text-2">
          {activeLabel} · {pendingLabel}
        </p>
        <Button
          size="sm"
          onClick={() => setInviteOpen(true)}
          iconStart={
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
              className="size-[15px]"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M19 8v6M22 11h-6" />
            </svg>
          }
        >
          Invitar miembro
        </Button>
      </div>

      <DataTable caption="Miembros del equipo" columns={columns} rows={members} />

      {soloOwner && (
        <p className="mt-3 text-[12px] leading-relaxed text-text-3">
          Trabajas en solitario. Puedes invitar a tu equipo cuando lo necesites.
        </p>
      )}

      {/* El muro de conversaciones se lee junto al equipo que gobierna (CP3 L):
          en Operación nadie alcanzaba a ver que hablaba de Bandeja. */}
      <ConversationVisibilityCard />

      <Modal
        open={inviteOpen}
        onClose={closeInvite}
        title="Invitar a tu equipo"
        subtitle="Le llega un correo para crear su cuenta y unirse."
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={closeInvite} disabled={busy}>
              Cancelar
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              disabled={busy}
              onClick={() => void submitInvite()}
            >
              {busy ? 'Enviando…' : 'Enviar invitación'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3.5">
          <FormField label="Correo electrónico" htmlFor="invite-email">
            <Input
              id="invite-email"
              type="email"
              placeholder="nombre@hospitalangeles.mx"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </FormField>
          <FormField
            label="Rol"
            htmlFor="invite-role"
            helper={
              canGrantAdmin
                ? 'Asesor atiende conversaciones. Administrador además gestiona el equipo.'
                : 'Asesor atiende conversaciones.'
            }
          >
            <Select
              id="invite-role"
              options={roleOptions}
              value={role}
              onChange={(e) => setRole(e.target.value)}
            />
          </FormField>
          <p className="flex gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2 text-[11px] leading-relaxed text-text-3">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
              className="mt-px size-3.5 flex-none"
            >
              <circle cx="12" cy="12" r="9.5" />
              <path d="M12 11v5M12 7.5h.01" />
            </svg>
            {canGrantAdmin
              ? 'Como Dueño puedes nombrar administradores. La propiedad del espacio se transfiere aparte, desde el menú del miembro.'
              : 'Solo el Dueño puede nombrar administradores. Tú puedes invitar asesores.'}
          </p>
        </div>
      </Modal>

      <Modal
        open={roleEdit !== null}
        onClose={() => setRoleEdit(null)}
        title="Cambiar rol"
        subtitle={roleEdit ? `Nuevo rol para ${roleEdit.name}.` : 'Elige el nuevo rol.'}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setRoleEdit(null)}>
              Cancelar
            </Button>
            <Button size="sm" className="ml-auto" onClick={() => void submitRoleEdit()}>
              Guardar
            </Button>
          </>
        }
      >
        <FormField label="Rol" htmlFor="edit-role">
          <Select
            id="edit-role"
            options={roleOptions}
            value={editRole}
            onChange={(e) => setEditRole(e.target.value)}
          />
        </FormField>
      </Modal>

      <Modal
        open={transferTarget !== null}
        onClose={() => setTransferTarget(null)}
        title="Transferir propiedad"
        subtitle={
          transferTarget ? `${transferTarget.name} será el nuevo Dueño.` : 'Elige al nuevo Dueño.'
        }
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => setTransferTarget(null)}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              disabled={busy}
              onClick={() => void submitTransfer()}
            >
              {busy ? 'Transfiriendo…' : 'Transferir propiedad'}
            </Button>
          </>
        }
      >
        <p className="text-[12.5px] leading-relaxed text-text-2">
          {transferTarget?.name} pasará a ser el Dueño del espacio y tú quedarás como administrador.
          Por seguridad cerraremos tu sesión y deberás iniciar sesión de nuevo. Esta acción solo se
          puede revertir si el nuevo Dueño te la regresa.
        </p>
      </Modal>

      <Modal
        open={mfaTarget !== null}
        onClose={() => setMfaTarget(null)}
        title="Restablecer MFA"
        subtitle={
          mfaTarget
            ? `${mfaTarget.name} configurará su verificación de nuevo al entrar.`
            : 'Elige a quién restablecer.'
        }
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setMfaTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="ml-auto"
              disabled={mfaReason.trim() === ''}
              onClick={() => void submitMfaReset()}
            >
              Restablecer MFA
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3.5">
          <p className="text-[12.5px] leading-relaxed text-text-2">
            Cerraremos todas sus sesiones y le enviaremos un correo. La próxima vez que inicie
            sesión configurará su verificación en dos pasos otra vez. Verifica primero su identidad
            por otro medio.
          </p>
          <FormField label="Motivo" htmlFor="mfa-reset-reason">
            <Input
              id="mfa-reset-reason"
              value={mfaReason}
              onChange={(e) => setMfaReason(e.target.value)}
              placeholder="Explica brevemente el motivo"
            />
          </FormField>
        </div>
      </Modal>

      <Modal
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        title={removeTarget?.kind === 'invite' ? 'Cancelar invitación' : 'Quitar del equipo'}
        subtitle={
          removeTarget?.kind === 'invite'
            ? `Se cancelará la invitación de ${removeTarget?.name ?? ''}.`
            : `Se quitará a ${removeTarget?.name ?? ''} del equipo.`
        }
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setRemoveTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="ml-auto"
              onClick={() => void submitRemove()}
            >
              {removeTarget?.kind === 'invite' ? 'Cancelar invitación' : 'Quitar del equipo'}
            </Button>
          </>
        }
      >
        <p className="text-[12.5px] leading-relaxed text-text-2">
          {removeTarget?.kind === 'invite'
            ? `${removeTarget?.name} ya no podrá unirse con esta invitación. Puedes volver a invitar a esta persona cuando quieras.`
            : `${removeTarget?.name} perderá el acceso a este espacio de trabajo y sus conversaciones volverán a la cola. Puedes volver a invitar a esta persona más adelante.`}
        </p>
      </Modal>
    </section>
  )
}

// ════ Facturación ════

function UsageMeterView({ meter }: { meter: UsageMeter }): JSX.Element {
  // No allowance on the plan ⇒ no denominator to show. A bar needs a limit to
  // mean anything, so an unlimited plan gets the count and nothing implied.
  if (meter.total === null) {
    return (
      <div>
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[12px] font-medium text-text">{meter.label}</span>
          <span className="font-mono text-[12px] font-semibold tabular-nums text-text">
            {meter.used.toLocaleString('es-MX')}
          </span>
        </div>
        <p className="mt-1.5 text-[11px] text-text-3">{meter.note} Tu plan no tiene límite.</p>
      </div>
    )
  }
  const pct = Math.min(100, Math.round((meter.used / meter.total) * 100))
  return (
    <div>
      <ProgressBar
        value={pct}
        // Over the allowance is information the client needs, not a full blue bar.
        tone={meter.used > meter.total ? 'warn' : meter.tone === 'ok' ? 'ok' : 'brand'}
        label={meter.label}
        valueText={`${meter.used.toLocaleString('es-MX')} / ${meter.total.toLocaleString('es-MX')}`}
      />
      <p className="mt-1.5 text-[11px] text-text-3">
        {meter.note}
        {meter.used > meter.total ? ' Estás por encima de tu plan.' : ''}
      </p>
    </div>
  )
}

function BillingMetaRow({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-line py-2 text-[12px] last:border-b-0">
      <span className="text-text-2">{k}</span>
      <span className="font-semibold text-text">{v}</span>
    </div>
  )
}

type ChipTone = 'ok' | 'warn' | 'sens' | 'info' | 'draft'

// Billing truth (subscriptions.status) → the words the owner reads. Deliberately
// NOT the same vocabulary as the operational tenants.status gate: "Suspendido"
// here means "we stopped the service because of payment", which is the only
// version of it a client should ever have to think about.
const SUB_STATUS: Record<SubscriptionStatus, { label: string; tone: ChipTone }> = {
  active: { label: 'Activo', tone: 'ok' },
  comped: { label: 'Cortesía', tone: 'info' },
  pending_payment_setup: { label: 'Falta configurar el pago', tone: 'warn' },
  past_due: { label: 'Pago vencido', tone: 'warn' },
  suspended: { label: 'Suspendido', tone: 'sens' },
  cancelled: { label: 'Cancelado', tone: 'draft' },
}

/** Stripe invoice status → Spanish. Unknown values fall back to the raw status. */
const INVOICE_STATUS: Record<string, { label: string; tone: ChipTone }> = {
  paid: { label: 'Pagada', tone: 'ok' },
  open: { label: 'Pendiente de pago', tone: 'warn' },
  draft: { label: 'Borrador', tone: 'draft' },
  void: { label: 'Anulada', tone: 'draft' },
  uncollectible: { label: 'Incobrable', tone: 'sens' },
}

/**
 * The CFDI's state, in words the clinic can act on.
 *
 * Every state a row can actually reach has an entry, because a chip reading
 * `issued_docs_missing` is worse than no chip. The internal vocabulary stays
 * internal: `pending` becomes "En proceso", not "pendiente", which the client
 * would read as something they owe us.
 */
const CFDI_STATUS: Record<string, { label: string; tone: ChipTone }> = {
  not_applicable: { label: 'No aplica', tone: 'draft' },
  pending: { label: 'En proceso', tone: 'draft' },
  stamping: { label: 'Emitiendo…', tone: 'draft' },
  pending_fiscal_data: { label: 'Faltan tus datos fiscales', tone: 'warn' },
  issued: { label: 'Emitida', tone: 'ok' },
  // The document exists at the SAT; we could not retrieve our copy. Saying
  // "Emitida" here and offering no download is the combination that reads as a
  // broken page, so it says what is true.
  issued_docs_missing: { label: 'Emitida, recuperando archivos', tone: 'warn' },
  superseded: { label: 'Sustituida', tone: 'draft' },
  cancellation_requested: { label: 'Cancelación en trámite', tone: 'warn' },
  cancelled: { label: 'Cancelada', tone: 'draft' },
  cancellation_rejected: { label: 'Cancelación rechazada', tone: 'sens' },
  cancellation_window_closed: { label: 'Ya no se puede cancelar', tone: 'draft' },
  failed: { label: 'No pudimos emitirla', tone: 'sens' },
}

/** ISO date (or datetime) → "12 de jul de 2026". Date-only strings are read at
 *  midday so the local timezone can't shift them to the previous day. */
function formatDay(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })
}

function paymentMethodLabel(pm: BillingPaymentMethod): string {
  const brand = pm.brand ? pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1) : 'Tarjeta'
  return pm.last4 ? `${brand} •••• ${pm.last4}` : brand
}

/** The subscription's real tier key ("standard"), capitalized for display.
 *  There is no separate marketing plan name to show — GET /billing never
 *  invents one, so this is the real value, not a canned string. */
function tierLabel(tierKey: string): string {
  return tierKey.length > 0 ? tierKey.charAt(0).toUpperCase() + tierKey.slice(1) : tierKey
}

/**
 * The clinic's own fiscal identity.
 *
 * These four fields were operator-only and shown here locked, under "escríbele
 * a tu gerente". CFDI 4.0 matches the razón social, RFC and código postal
 * against the SAT padrón EXACTLY, and the clinic is the only party who knows
 * what their constancia says — so a typo they could not fix was a support
 * round-trip during which they had no invoice.
 *
 * We ask for the four values, never for the Constancia PDF: the SAT has
 * clarified that a supplier may not require it.
 */
function FiscalIdentityCard(): JSX.Element {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['settings'], queryFn: getSettings })
  const fiscal = data?.fiscal
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<FiscalEdit>({})
  const [problems, setProblems] = useState<FiscalProblemView[]>([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  if (!fiscal) return <></>

  // Spread rather than pass `error={...}`: FormField is typed under
  // exactOptionalPropertyTypes, so an explicit `undefined` is not the same as
  // absent.
  const errProps = (field: keyof FiscalEdit): { error?: string } => {
    const message = problems.find((p) => p.field === field)?.message
    return message === undefined ? {} : { error: message }
  }

  function begin(): void {
    setForm({
      legalName: fiscal!.legalName,
      rfc: fiscal!.rfc,
      postalCode: fiscal!.postalCode,
      fiscalRegimen: fiscal!.fiscalRegimen,
      cfdiUse: fiscal!.cfdiUse || 'G03',
    })
    setProblems([])
    setError('')
    setOpen(true)
  }

  async function save(): Promise<void> {
    setBusy(true)
    setProblems([])
    setError('')
    try {
      await saveFiscal(form)
      await qc.invalidateQueries({ queryKey: ['settings'] })
      setOpen(false)
    } catch (err) {
      // Field-level messages when the API gave them; a single message otherwise.
      const ps = fiscalProblemsOf(err)
      if (ps.length > 0) setProblems(ps)
      else setError(apiErrorMessage(err, 'No pudimos guardar tus datos fiscales.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section
      id="datos-fiscales"
      aria-labelledby="fiscal-title"
      className="rounded-xl border border-line bg-surface p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 id="fiscal-title" className="text-[13.5px] font-bold text-text">
            Datos fiscales
          </h2>
          <p className="mt-1 text-[12px] text-text-2">
            Con estos emitimos tu factura fiscal (CFDI). Deben coincidir exactamente con tu
            constancia del SAT.
          </p>
        </div>
        <Button variant="secondary" onClick={begin}>
          {fiscal.canInvoice ? 'Editar' : 'Completar'}
        </Button>
      </div>

      {!fiscal.canInvoice && (
        <div className="mt-3">
          <Banner tone="warn">
            Faltan datos para poder emitir tu CFDI. Sin ellos solo recibes el comprobante de pago.
          </Banner>
        </div>
      )}

      <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        <FiscalRow k="Razón social" v={fiscal.legalName} />
        <FiscalRow k="RFC" v={fiscal.rfc} mono />
        <FiscalRow k="Código postal" v={fiscal.postalCode} />
        <FiscalRow
          k="Régimen fiscal"
          v={FISCAL_REGIMENS.find((r) => r.code === fiscal.fiscalRegimen)?.name ?? ''}
        />
        <FiscalRow
          k="Uso del CFDI"
          v={CFDI_USES.find((u) => u.code === fiscal.cfdiUse)?.name ?? ''}
        />
      </dl>

      {open && (
        <Modal
          open
          onClose={() => setOpen(false)}
          title="Datos fiscales"
          subtitle="Cópialos de tu constancia de situación fiscal. No necesitamos el PDF."
          footer={
            <>
              <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
                Cancelar
              </Button>
              <Button onClick={() => void save()} disabled={busy}>
                {busy ? 'Guardando…' : 'Guardar'}
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3.5">
            {error !== '' && <Banner tone="sens">{error}</Banner>}
            <FormField
              label="Razón social"
              htmlFor="fi-legal"
              {...errProps('legalName')}
              helper="Sin S.A. de C.V. ni S.C. — el SAT la registra sin el régimen societal."
            >
              <Input
                id="fi-legal"
                value={form.legalName ?? ''}
                onChange={(e) => setForm({ ...form, legalName: e.target.value })}
              />
            </FormField>
            <FormField
              label="RFC"
              htmlFor="fi-rfc"
              {...errProps('rfc')}
              helper="12 caracteres si eres empresa, 13 si eres persona física."
            >
              <Input
                id="fi-rfc"
                className="font-mono"
                value={form.rfc ?? ''}
                onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
              />
            </FormField>
            <FormField label="Código postal" htmlFor="fi-cp" {...errProps('postalCode')}>
              <Input
                id="fi-cp"
                value={form.postalCode ?? ''}
                onChange={(e) =>
                  setForm({ ...form, postalCode: e.target.value.replace(/\D/g, '').slice(0, 5) })
                }
              />
            </FormField>
            <FormField label="Régimen fiscal" htmlFor="fi-regimen" {...errProps('fiscalRegimen')}>
              <Select
                id="fi-regimen"
                value={form.fiscalRegimen ?? ''}
                onChange={(e) => setForm({ ...form, fiscalRegimen: e.target.value })}
                options={[
                  { value: '', label: 'Selecciona uno' },
                  ...FISCAL_REGIMENS.map((r) => ({
                    value: r.code,
                    label: `${r.code} — ${r.name}`,
                  })),
                ]}
              />
            </FormField>
            <FormField label="Uso del CFDI" htmlFor="fi-uso" {...errProps('cfdiUse')}>
              <Select
                id="fi-uso"
                value={form.cfdiUse ?? ''}
                onChange={(e) => setForm({ ...form, cfdiUse: e.target.value })}
                options={CFDI_USES.map((u) => ({ value: u.code, label: `${u.code} — ${u.name}` }))}
              />
            </FormField>
          </div>
        </Modal>
      )}
    </section>
  )
}

function FiscalRow({ k, v, mono }: { k: string; v: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex justify-between gap-3 border-b border-line/60 py-1.5 last:border-0">
      <dt className="text-[12px] text-text-2">{k}</dt>
      <dd className={`text-[12.5px] text-text ${mono === true ? 'font-mono' : ''}`}>
        {v !== '' ? v : <span className="text-text-3">Por registrar</span>}
      </dd>
    </div>
  )
}

function BillingSection({
  usage,
  manager,
}: {
  usage: UsageMeter[]
  manager: ManagerInfo
}): JSX.Element {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [portalBusy, setPortalBusy] = useState(false)
  const [payingId, setPayingId] = useState<string | null>(null)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelBusy, setCancelBusy] = useState(false)
  /** `<invoiceId>:<kind>`, so the two buttons on one row disable independently. */
  const [cfdiBusy, setCfdiBusy] = useState<string | null>(null)
  const account = useQuery({ queryKey: ['billing'], queryFn: getBilling, retry: false })

  /**
   * Save the stamped CFDI.
   *
   * The failure has to be visible. A download button that does nothing is the
   * most confusing state a file control can be in, and this one is fetched with
   * a token rather than being a plain link, so nothing else would surface it.
   */
  async function saveCfdi(invoiceId: string, kind: 'xml' | 'pdf'): Promise<void> {
    setCfdiBusy(`${invoiceId}:${kind}`)
    try {
      await downloadCfdi(invoiceId, kind)
    } catch {
      toast.error(`No pudimos descargar el ${kind.toUpperCase()}`, {
        description: 'Inténtalo de nuevo en un momento.',
      })
    } finally {
      setCfdiBusy(null)
    }
  }

  // The action the whole dunning ladder asks for. With collection_method
  // 'send_invoice' Stripe charges nothing automatically — paying happens on
  // Stripe's hosted invoice page, so this is the ONLY way an owner settles up.
  async function payInvoice(inv: BillingInvoiceRow): Promise<void> {
    setPayingId(inv.id)
    try {
      window.location.assign(inv.payUrl ?? (await getInvoicePayUrl(inv.id)))
    } catch (err) {
      toast.error('No pudimos abrir el pago', {
        description: apiErrorMessage(err, 'Inténtalo de nuevo o escríbele a tu gerente de cuenta.'),
      })
      setPayingId(null)
    }
  }

  async function confirmCancel(): Promise<void> {
    setCancelBusy(true)
    try {
      const r = await requestCancellation()
      await qc.invalidateQueries({ queryKey: ['billing'] })
      toast.ok(
        r.status === 'already_scheduled'
          ? 'Ya tenías la cancelación programada'
          : 'Listo, tu servicio termina al final del periodo pagado',
      )
      setCancelOpen(false)
    } catch (err) {
      // Inside the minimum term this is a conversation, not a refusal.
      const code = (err as AxiosError<{ error?: { code?: string } }>)?.response?.data?.error?.code
      if (code === 'requires_review') {
        toast.error('Tu contrato tiene un plazo mínimo', {
          description: 'Tu gerente de cuenta puede revisarlo contigo desde Mensajes.',
        })
        setCancelOpen(false)
      } else {
        toast.error('No pudimos programar la cancelación', {
          description: apiErrorMessage(err, 'Inténtalo de nuevo.'),
        })
      }
    } finally {
      setCancelBusy(false)
    }
  }

  const invoiceColumns: DataTableColumn<BillingInvoiceRow>[] = [
    {
      key: 'issued',
      label: 'Factura',
      render: (inv) => (
        <span>
          <span className="block font-semibold text-text">
            {inv.folio ?? formatDay(inv.createdAt)}
          </span>
          <span className="block text-[11px] text-text-3">
            {inv.paidAt ? `Pagada el ${formatDay(inv.paidAt)}` : `Vence ${formatDay(inv.dueDate)}`}
          </span>
        </span>
      ),
    },
    {
      key: 'period',
      label: 'Periodo',
      render: (inv) => (
        <span className="text-text-2">
          {inv.periodStart && inv.periodEnd
            ? `${formatDay(inv.periodStart)} – ${formatDay(inv.periodEnd)}`
            : '—'}
        </span>
      ),
    },
    {
      key: 'amount',
      label: 'Monto',
      render: (inv) => (
        <span>
          <span className="block font-mono font-semibold tabular-nums text-text">
            {formatMxn(inv.amountTotalMxn, inv.currency)}
          </span>
          {/* Show the split, not just "IVA incluido" — the client's accountant
              needs the base and the tax as separate numbers. */}
          {inv.amountTaxMxn > 0 && (
            <span className="block text-[10.5px] text-text-3">
              {formatMxn(inv.amountSubtotalMxn, inv.currency)} + IVA{' '}
              {formatMxn(inv.amountTaxMxn, inv.currency)}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (inv) => {
        const s = INVOICE_STATUS[inv.status] ?? { label: inv.status, tone: 'draft' as ChipTone }
        return (
          <Chip tone={s.tone} dot>
            {s.label}
          </Chip>
        )
      },
    },
    {
      key: 'cfdi',
      label: 'Factura fiscal',
      render: (inv) => {
        // Optional-chained on purpose. A paid invoice with no CFDI state
        // predates fiscal invoicing, and a browser holding a `/billing`
        // response cached from before this shipped has no `cfdi` key at all —
        // a table that throws mid-deploy takes the whole billing page with it.
        // Both cases mean the same thing to the reader: nothing to show.
        const status = inv.cfdi?.status ?? null
        if (status === null || status === 'not_applicable') {
          return <span className="text-text-3">—</span>
        }
        const s = CFDI_STATUS[status] ?? {
          label: status,
          tone: 'draft' as ChipTone,
        }
        return (
          <span className="block">
            <Chip tone={s.tone} dot>
              {s.label}
            </Chip>
            {/* Only shown when the clinic is the one who can fix it. The API
                returns `reason` for no other state, so a PAC error on our side
                never reaches them as homework. */}
            {(inv.cfdi?.reason ?? null) !== null && (
              <span className="mt-1 block max-w-[26ch] text-[10.5px] leading-snug text-text-3">
                {inv.cfdi?.reason}
              </span>
            )}
            {(inv.cfdi?.uuid ?? null) !== null && (
              <span className="mt-1 block font-mono text-[10px] text-text-3">{inv.cfdi?.uuid}</span>
            )}
          </span>
        )
      },
    },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      render: (inv) => (
        <span className="flex items-center justify-end gap-2">
          {inv.payUrl !== null && (
            <Button size="sm" disabled={payingId === inv.id} onClick={() => void payInvoice(inv)}>
              {payingId === inv.id ? 'Abriendo…' : 'Pagar'}
            </Button>
          )}
          {/* The XML is the legally valid document; the PDF is its readable
              form. An accountant needs both, so neither is hidden behind the
              other. */}
          {inv.cfdi?.canDownload === true &&
            (['xml', 'pdf'] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                disabled={cfdiBusy === `${inv.id}:${kind}`}
                onClick={() => void saveCfdi(inv.id, kind)}
                className="rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-text-2 transition-colors hover:bg-hover hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue disabled:opacity-50"
              >
                {cfdiBusy === `${inv.id}:${kind}` ? '…' : kind.toUpperCase()}
              </button>
            ))}
          {/* Stripe's payment receipt. It is NOT the CFDI, and labelling both
              "PDF" put two buttons on the same row that mean different things
              to an accountant — one is proof of payment, the other is the
              deductible document. */}
          {inv.pdfUrl !== null && (
            <a
              href={inv.pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-text-2 transition-colors hover:bg-hover hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue"
            >
              Recibo
            </a>
          )}
        </span>
      ),
    },
  ]

  async function goToPortal(): Promise<void> {
    setPortalBusy(true)
    try {
      window.location.assign(await openBillingPortal())
    } catch (err) {
      toast.error('No pudimos abrir el portal de pago', {
        description: apiErrorMessage(err, 'Inténtalo de nuevo en un momento.'),
      })
      setPortalBusy(false)
    }
  }

  // Usage meters always render — they come from /settings and every role that
  // reaches this tab can see them. Only the money needs billing.read.
  const usageCard = (
    <section id="uso" className="rounded-xl border border-line bg-surface p-5">
      <h3 className="text-[13.5px] font-bold text-text">Uso este mes</h3>
      <p className="mt-0.5 text-[12px] text-text-3">Al día de hoy</p>
      <div className="mt-4 flex flex-col gap-4">
        {usage.map((u) => (
          <UsageMeterView key={u.id} meter={u} />
        ))}
      </div>
    </section>
  )

  if (account.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label="Cargando tu facturación…" />
      </div>
    )
  }

  // 403 `permission_denied` = a role without billing.read (only the Dueño has
  // it). That's a rule, not a failure, so say so plainly instead of showing an
  // error.
  //
  // The error CODE decides, not the status: a Dueño who has not enrolled a
  // second factor also gets a 403, and telling the actual Dueño "solo el Dueño
  // puede ver la facturación" is simply false. That 403 carries
  // `auth_mfa_enrollment_required`, and the axios interceptor is already
  // navigating to /account/security on it — but `window.location.assign` is not
  // instantaneous, and this is the one query on the page with `retry: false`, so
  // it painted the wrong sentence first and won the race every time. Rendering
  // nothing lets the bounce land (checkpoint-2 F1).
  if (account.isError) {
    const err = account.error as AxiosError<{ error?: { code?: string } }> | undefined
    const status = err?.response?.status
    const code = err?.response?.data?.error?.code
    if (status === 403 && code === 'auth_mfa_enrollment_required') {
      return (
        <div className="flex justify-center py-12">
          <Spinner label="Redirigiendo a tu configuración de seguridad…" />
        </div>
      )
    }
    if (status === 403) {
      return (
        <div className="flex flex-col gap-4">
          <p className="rounded-xl border border-line bg-surface px-4 py-6 text-center text-[12.5px] text-text-2">
            Solo el Dueño de la cuenta puede ver la facturación.
          </p>
          {usageCard}
        </div>
      )
    }
    return (
      <QueryError
        message="No pudimos cargar tu facturación."
        onRetry={() => void account.refetch()}
      />
    )
  }

  const sub = account.data?.subscription ?? null
  const invoices = account.data?.invoices ?? []
  const methods = account.data?.paymentMethods ?? []
  const status = sub ? SUB_STATUS[sub.status] : null
  const defaultMethod = methods.find((m) => m.isDefault) ?? methods[0]
  // The invoice the "Pagar ahora" banner acts on — the oldest unpaid one, since
  // that is the debt that suspended them.
  const openInvoice = [...invoices].reverse().find((i) => i.status === 'open' && i.payUrl !== null)

  return (
    <div className="flex flex-col gap-4">
      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="¿Cancelar tu servicio?"
        subtitle={
          sub?.contractEndMinPassed === false
            ? 'Tu contrato tiene un plazo mínimo. Lo revisamos contigo antes de cancelar.'
            : 'Tu agente sigue respondiendo hasta el final del periodo que ya pagaste. No hay reembolso parcial.'
        }
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCancelOpen(false)}
              disabled={cancelBusy}
            >
              No, quedarme
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              disabled={cancelBusy}
              onClick={() => void confirmCancel()}
            >
              {cancelBusy ? 'Programando…' : 'Sí, cancelar al final del periodo'}
            </Button>
          </>
        }
      >
        <p className="text-[12.5px] leading-relaxed text-text-2">
          Después de esa fecha tu agente deja de responder y tu equipo pierde acceso al panel.
          Puedes volver a activarlo escribiéndole a tu gerente de cuenta.
        </p>
      </Modal>

      {sub && (sub.status === 'past_due' || sub.status === 'suspended') && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-warn/25 bg-warn-bg px-4 py-3">
          <Chip tone="warn" dot>
            Acción requerida
          </Chip>
          <p className="min-w-0 flex-1 text-[12px] leading-snug text-text-2">
            Tienes un pago pendiente. Actualiza tu método de pago para mantener tu agente activo.
          </p>
          {openInvoice !== undefined && (
            <Button
              size="sm"
              disabled={payingId !== null}
              onClick={() => void payInvoice(openInvoice)}
            >
              {payingId !== null ? 'Abriendo…' : 'Pagar ahora'}
            </Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        {/* plan card */}
        <section id="plan" className="rounded-xl border border-line bg-surface p-5">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.05em] text-text-3">
            Tu plan
          </div>
          {sub ? (
            <>
              <div className="mt-1.5 flex items-center gap-2.5">
                <span className="text-[20px] font-extrabold tracking-[-0.02em] text-text">
                  {tierLabel(sub.tierKey)}
                </span>
                {status && (
                  <Chip tone={status.tone} dot>
                    {status.label}
                  </Chip>
                )}
              </div>
              <p className="mt-1 text-[12.5px] text-text-2">
                {sub.priceMxn !== null ? (
                  <>
                    <b className="font-semibold text-text">
                      {formatMxn(sub.priceMxn, sub.currency)}
                    </b>{' '}
                    al mes + IVA ({formatMxn(Math.round(sub.priceMxn * 1.16), sub.currency)} en
                    total)
                  </>
                ) : (
                  'Precio acordado con tu gerente de cuenta.'
                )}
              </p>
              <div className="mt-3.5 flex flex-col">
                <BillingMetaRow
                  k="Ciclo de facturación"
                  v={sub.billingCycle === 'monthly' ? 'Mensual' : 'Anual'}
                />
                <BillingMetaRow k="Próxima factura" v={formatDay(sub.currentPeriodEnd)} />
                <BillingMetaRow k="Cliente desde" v={formatDay(sub.contractStart)} />
                <BillingMetaRow
                  k="Método de pago"
                  v={defaultMethod ? paymentMethodLabel(defaultMethod) : 'Sin registrar'}
                />
              </div>
              {sub.cancelAtPeriodEnd && (
                <p className="mt-3.5 rounded-xl border border-warn/25 bg-warn-bg px-3 py-2 text-[11.5px] leading-relaxed text-text-2">
                  Tu servicio termina el {formatDay(sub.currentPeriodEnd)}. Hasta esa fecha todo
                  sigue funcionando igual.
                </p>
              )}
              {sub.hasStripeCustomer ? (
                <div className="mt-3.5 flex flex-col gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={portalBusy}
                    onClick={() => void goToPortal()}
                  >
                    {portalBusy ? 'Abriendo…' : 'Gestionar método de pago'}
                  </Button>
                  {!sub.cancelAtPeriodEnd && sub.status !== 'cancelled' && (
                    <Button variant="ghost" size="sm" onClick={() => setCancelOpen(true)}>
                      Cancelar servicio
                    </Button>
                  )}
                </div>
              ) : (
                <p className="mt-3.5 text-[11.5px] leading-relaxed text-text-3">
                  Tu cuenta no tiene cobros en línea. Tu gerente de cuenta coordina la facturación.
                </p>
              )}
            </>
          ) : (
            <p className="mt-2 text-[12.5px] leading-relaxed text-text-2">
              Todavía no hay un plan activo en tu cuenta. Tu gerente de cuenta lo configura contigo.
            </p>
          )}

          <div className="mt-3.5 flex items-start gap-3 rounded-xl border border-brand-blue/15 bg-brand-blue/[0.05] p-3">
            <span
              aria-hidden
              className="grid size-8 flex-none place-items-center rounded-full bg-[image:var(--brand-gradient)] text-[11px] font-bold text-white"
            >
              {manager.initials}
            </span>
            <p className="min-w-0 flex-1 text-[11.5px] leading-relaxed text-text-2">
              ¿Necesitas cambiar de plan o ajustar tu cuenta? Lo coordina{' '}
              <b className="font-semibold text-text">{manager.name}</b>, tu gerente de cuenta.
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="flex-none"
              onClick={() => void navigate('/mensajes')}
            >
              Escríbele
            </Button>
          </div>
        </section>

        {usageCard}
      </div>

      <FiscalIdentityCard />

      <section id="facturas" aria-labelledby="inv-history-title">
        <h2 id="inv-history-title" className="mb-3 text-[13.5px] font-bold text-text">
          Historial de facturas
        </h2>
        {invoices.length > 0 ? (
          <>
            <DataTable caption="Historial de facturas" columns={invoiceColumns} rows={invoices} />
            <p className="mt-2 text-[11.5px] text-text-3">
              El PDF es tu comprobante de pago. La factura fiscal (CFDI) llega por separado, con tus
              datos fiscales.
            </p>
          </>
        ) : (
          <EmptyStateCard
            kind="day1"
            headingLevel={3}
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M6 2h12v20l-3-2-3 2-3-2-3 2V2ZM9 7h6M9 11h6" />
              </svg>
            }
            title="Aún no hay facturas"
            body="La primera aparece aquí en cuanto se emita; te llega también por correo al pagar cada periodo."
          />
        )}
      </section>
    </div>
  )
}

// ════ Registro de actividad / audit log ════

function ActorAvatar({ event }: { event: AuditEvent }): JSX.Element {
  if (event.actorKind === 'system') {
    return (
      <span
        aria-hidden
        className="grid size-9 flex-none place-items-center rounded-full bg-field text-text-2"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          className="size-[15px]"
        >
          <rect x="2.5" y="5" width="19" height="14" rx="2.4" />
          <path d="M2.5 10h19" />
        </svg>
      </span>
    )
  }
  return (
    <span
      aria-hidden
      className={`grid size-9 flex-none place-items-center rounded-full text-[11px] font-bold ${
        event.actorKind === 'staff'
          ? 'bg-[image:var(--brand-gradient)] text-white'
          : 'bg-field text-text-2'
      }`}
    >
      {event.actorInitials}
    </span>
  )
}

function AuditRow({
  event,
  open,
  onToggle,
}: {
  event: AuditEvent
  open: boolean
  onToggle: () => void
}): JSX.Element {
  const showBadge = event.actorKind !== 'user'
  return (
    <li className="border-b border-line last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-blue"
      >
        <ActorAvatar event={event} />
        <span className="min-w-0 flex-1">
          <span className="block text-[12.5px] leading-snug text-text">
            {event.actorKind !== 'system' && <b className="font-semibold">{event.actorName} </b>}
            {event.action}
          </span>
          <span className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-text-2">
            {showBadge && (
              <span className="rounded-[5px] bg-field px-1.5 py-px text-[9.5px] font-bold uppercase tracking-[0.04em] text-text-2">
                {event.context}
              </span>
            )}
            <span>
              {event.actorKind === 'user' ? `${event.context} · ${event.time}` : event.time}
            </span>
          </span>
        </span>
        <span className="hidden flex-none rounded-pill border border-line bg-hover px-2 py-0.5 text-[10.5px] font-semibold text-text-2 sm:inline-block">
          {event.category}
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          className={`size-4 flex-none text-text-3 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path d="m7 9 5 5 5-5" />
        </svg>
      </button>
      {open && (
        <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 border-t border-line bg-surface-2 px-4 py-3.5 sm:grid-cols-2">
          {event.detail.map((d) => (
            <div key={d.k} className="flex gap-2 text-[11.5px]">
              <dt className="w-28 flex-none text-text-3">{d.k}</dt>
              <dd className={`min-w-0 flex-1 text-text ${d.mono ? 'font-mono text-[11px]' : ''}`}>
                {d.v}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  )
}

function exportActivityCsv(rows: AuditEvent[]): void {
  const head = ['Fecha', 'Acción', 'Persona', 'Contexto', 'Categoría', 'Folio']
  const esc = (s: string): string => `"${s.replace(/"/g, '""')}"`
  const lines = rows.map((e) =>
    [e.occurredAt, e.action, e.actorName, e.context, e.category, e.id].map(esc).join(','),
  )
  const csv = [head.map(esc).join(','), ...lines].join('\n')
  // Prefix a UTF-8 BOM so Excel reads the accents correctly.
  const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = 'registro-actividad.csv'
  a.click()
  URL.revokeObjectURL(url)
}

function ActivitySection({ events }: { events: AuditEvent[] }): JSX.Element {
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('')
  const [actor, setActor] = useState('')
  const [days, setDays] = useState('') // '' = todos, '30', '90'
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // The filter options come from what's actually present in the loaded events.
  const categories = useMemo(
    () =>
      Array.from(new Set(events.map((e) => e.category))).sort((a, b) => a.localeCompare(b, 'es')),
    [events],
  )
  const actors = useMemo(
    () =>
      Array.from(new Set(events.map((e) => e.actorName))).sort((a, b) => a.localeCompare(b, 'es')),
    [events],
  )

  const cutoff = days === '' ? 0 : Date.now() - Number(days) * 24 * 60 * 60 * 1000

  const filtered = events.filter(
    (e) =>
      (category === '' || e.category === category) &&
      (actor === '' || e.actorName === actor) &&
      (cutoff === 0 || new Date(e.occurredAt).getTime() >= cutoff) &&
      matchesSearch(
        `${e.actorName} ${e.action} ${e.context} ${e.category} ${e.detail.map((d) => d.v).join(' ')}`,
        query,
      ),
  )
  const hasFilters = query !== '' || category !== '' || actor !== '' || days !== ''

  function toggle(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-[60ch] text-[12.5px] text-text-2">
          Cada acción realizada en tu espacio de trabajo, tuya o del equipo de INHERTA. Nada se
          borra.
        </p>
        <Button
          variant="secondary"
          size="sm"
          className="flex-none"
          disabled={filtered.length === 0}
          onClick={() => exportActivityCsv(filtered)}
        >
          Exportar
        </Button>
      </div>

      <FilterBar className="mb-4">
        <SearchInput
          aria-label="Buscar en el registro"
          placeholder="Buscar en el registro"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="w-full sm:w-44">
          <Select
            aria-label="Filtrar por tipo de acción"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            options={[
              { value: '', label: 'Todas las acciones' },
              ...categories.map((c) => ({ value: c, label: c })),
            ]}
          />
        </div>
        <div className="w-full sm:w-44">
          <Select
            aria-label="Filtrar por persona"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            options={[
              { value: '', label: 'Todas las personas' },
              ...actors.map((a) => ({ value: a, label: a })),
            ]}
          />
        </div>
        <div className="w-full sm:w-40">
          <Select
            aria-label="Filtrar por fecha"
            value={days}
            onChange={(e) => setDays(e.target.value)}
            options={[
              { value: '', label: 'Cualquier fecha' },
              { value: '30', label: 'Últimos 30 días' },
              { value: '90', label: 'Últimos 90 días' },
            ]}
          />
        </div>
        {hasFilters && (
          <FilterButton
            onClick={() => {
              setQuery('')
              setCategory('')
              setActor('')
              setDays('')
            }}
          >
            Limpiar
          </FilterButton>
        )}
      </FilterBar>

      {filtered.length === 0 ? (
        <p className="rounded-xl border border-line bg-surface px-4 py-10 text-center text-[12.5px] text-text-3">
          Ningún evento coincide con tu búsqueda.
        </p>
      ) : (
        <ul className="overflow-hidden rounded-xl border border-line bg-surface">
          {filtered.map((e) => (
            <AuditRow
              key={e.id}
              event={e}
              open={expanded.has(e.id)}
              onToggle={() => toggle(e.id)}
            />
          ))}
        </ul>
      )}

      <p className="mt-3 text-[11.5px] text-text-3">
        Mostrando {filtered.length}
        {hasFilters ? ` de ${events.length}` : ''} eventos más recientes.
      </p>
    </section>
  )
}

export function SettingsPage(): JSX.Element {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
  })
  // The tab lives in the URL so notifications (and any other deep link) can send
  // someone straight to the section they need — a "pago pendiente" notice that
  // dropped the owner on Perfil would be telling them to go somewhere without
  // taking them there. Unknown values fall back to Perfil.
  // C7: land on the exact billing section when the URL carries a hash
  // (#uso, #plan, #facturas, #datos-fiscales). The tab content mounts after
  // data loads, so native anchor jumping never fires on its own.
  const { hash } = useLocation()
  useEffect(() => {
    if (!hash) return
    const el = document.getElementById(hash.slice(1))
    if (el) el.scrollIntoView({ block: 'start' })
  })

  const tabs = SETTINGS_TABS

  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab')
  const tab = tabs.some((t) => t.id === requested) ? requested! : 'perfil'
  const setTab = (next: string): void => {
    setSearchParams(next === 'perfil' ? {} : { tab: next }, { replace: true })
  }

  if (isError) {
    return (
      <div data-testid="settings" className="mx-auto w-full max-w-4xl py-10">
        <QueryError message="No pudimos cargar tu configuración." onRetry={() => void refetch()} />
      </div>
    )
  }
  if (isLoading || !data) {
    return (
      <div data-testid="settings" className="mx-auto w-full max-w-4xl py-10">
        <Spinner label="Cargando configuración…" />
      </div>
    )
  }

  return (
    <div data-testid="settings" className="mx-auto w-full max-w-4xl">
      <PageHeader title="Configuración" subtitle="Los datos de tu espacio de trabajo." />

      <div className="mt-4">
        <Tabs
          tabs={tabs}
          activeId={tab}
          onChange={setTab}
          idBase="settings"
          aria-label="Secciones de configuración"
        />
      </div>

      <div className="mt-5">
        <TabPanel idBase="settings" tabId={tab}>
          {tab === 'perfil' && (
            <div className="flex flex-col gap-5">
              <ProfileSection fields={data.profile} />
              <ChannelsSection channels={data.channels} />
            </div>
          )}
          {tab === 'campos' && <FieldsSection />}
          {tab === 'operacion' && <OperationsSection />}
          {tab === 'embudos' && <PipelinesSection />}
          {/* Solicitudes ARCO is deliberately NOT here. Requests arrive at
              privacidad@inherta.com, which only INHERTA can read, so only
              INHERTA can act on them — the surface lives in the operator
              console. The clinic's Privacidad tab is its own consent notice,
              which IS theirs to control. */}
          {tab === 'privacidad' && <ConsentSettings />}
          {tab === 'equipo' && <TeamSection members={data.team} />}
          {tab === 'facturacion' && <BillingSection usage={data.usage} manager={data.manager} />}
          {tab === 'actividad' && <ActivitySection events={data.activity} />}
        </TabPanel>
      </div>
    </div>
  )
}
